export interface VehicleData {
  [make: string]: { [model: string]: number[] };
}

export interface VehicleMake {
  make: string;
  models: string[];
  category: string;
}

class VehicleService {
  private baseUrl: string;

  constructor() {
    // Use VPSReporting's API configuration
    if (process.env.REACT_APP_API_URI) {
      this.baseUrl = process.env.REACT_APP_API_URI;
    } else {
      // Default to VPSReporting server for development
      this.baseUrl = 'http://localhost:5001';
    }
  }

  async getAllVehicles(): Promise<VehicleData> {
    try {
      const response = await fetch(`${this.baseUrl}/vehicles`);

      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
      // Fallback to static data if API is not available
      try {
        const { vehicleData } = await import('../data/vehicleData');
        return vehicleData;
      } catch (fallbackError) {
        console.error('Error loading fallback data:', fallbackError);
        return {};
      }
    }
  }

  async getVehicleMakes(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/vehicles/makes`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching vehicle makes:', error);
      // Fallback to static data
      try {
        const { vehicleData } = await import('../data/vehicleData');
        return Object.keys(vehicleData).sort();
      } catch (fallbackError) {
        console.error('Error loading fallback makes:', fallbackError);
        return [];
      }
    }
  }

  async getVehicleModels(make: string): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/vehicles/${encodeURIComponent(make)}/models`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching models for make:', error);
      // Fallback to static data
      try {
        const { vehicleData } = await import('../data/vehicleData');
        const makeData = vehicleData[make];
        if (!makeData) return [];

        return Object.keys(makeData).sort();
      } catch (fallbackError) {
        console.error('Error loading fallback models:', fallbackError);
        return [];
      }
    }
  }

  async getVehicleYears(make: string, model: string): Promise<number[]> {
    try {
      const response = await fetch(`${this.baseUrl}/vehicles/${encodeURIComponent(make)}/${encodeURIComponent(model)}/years`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const years = await response.json();
      return years.sort((a: number, b: number) => b - a); // Sort years descending (newest first)
    } catch (error) {
      console.error('Error fetching years for make/model:', error);
      // Fallback to static data
      try {
        const { vehicleData } = await import('../data/vehicleData');
        const makeData = vehicleData[make];
        if (!makeData || !model) return [];

        const modelData = makeData[model];
        if (!modelData) return [];

        return modelData.sort((a, b) => b - a);
      } catch (fallbackError) {
        console.error('Error loading fallback years:', fallbackError);
        return [];
      }
    }
  }

  async getVehiclesByCategory(): Promise<{ [category: string]: VehicleData }> {
    try {
      const response = await fetch(`${this.baseUrl}/vehicles/categories`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching vehicles by category:', error);
      // Fallback to static data
      try {
        const { vehicleData } = await import('../data/vehicleData');
        return { 'All': vehicleData };
      } catch (fallbackError) {
        console.error('Error loading fallback categories:', fallbackError);
        return {};
      }
    }
  }
}

// Export a singleton instance
export const vehicleService = new VehicleService();
export default vehicleService;